import { TrendingUp, Minus, TrendingDown, type LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import type { EvolutionStatus } from '@/types'

export const EVOLUTION_ORDER: EvolutionStatus[] = ['improving', 'stable', 'worsening']

export const EVOLUTION_CONFIG: Record<
  EvolutionStatus,
  { label: string; icon: LucideIcon; badge: string; dot: string; ring: string }
> = {
  improving: {
    label: 'Mejorando',
    icon: TrendingUp,
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    dot: 'bg-emerald-500',
    ring: 'border-emerald-400 bg-emerald-50 text-emerald-800',
  },
  stable: {
    label: 'Estable',
    icon: Minus,
    badge: 'bg-sky-100 text-sky-800 border-sky-200',
    dot: 'bg-sky-500',
    ring: 'border-sky-400 bg-sky-50 text-sky-800',
  },
  worsening: {
    label: 'Empeorando',
    icon: TrendingDown,
    badge: 'bg-rose-100 text-rose-800 border-rose-200',
    dot: 'bg-rose-500',
    ring: 'border-rose-400 bg-rose-50 text-rose-800',
  },
}

export function EvolutionStatusBadge({
  status,
  className,
}: {
  status?: EvolutionStatus | null
  className?: string
}) {
  if (!status || !EVOLUTION_CONFIG[status]) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded border border-ml-outline-variant/30 px-2 py-0.5 text-[10px] font-medium text-ml-on-surface-variant/60',
          className,
        )}
      >
        Sin marcar
      </span>
    )
  }
  const cfg = EVOLUTION_CONFIG[status]
  const Icon = cfg.icon
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
        cfg.badge,
        className,
      )}
    >
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  )
}

/** Three-way selector for the doctor to mark this visit's evolution. */
export function EvolutionStatusPicker({
  value,
  onChange,
  disabled = false,
}: {
  value?: EvolutionStatus | '' | null
  onChange: (v: EvolutionStatus | '') => void
  disabled?: boolean
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {EVOLUTION_ORDER.map((status) => {
        const cfg = EVOLUTION_CONFIG[status]
        const Icon = cfg.icon
        const active = value === status
        return (
          <button
            key={status}
            type="button"
            disabled={disabled}
            onClick={() => onChange(active ? '' : status)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60',
              active
                ? cfg.ring
                : 'border-ml-outline-variant/30 bg-ml-surface text-ml-on-surface-variant hover:border-ml-outline-variant/60',
            )}
            aria-pressed={active}
          >
            <Icon className="h-3.5 w-3.5" />
            {cfg.label}
          </button>
        )
      })}
    </div>
  )
}
